import type { ReactNode } from "react";
import { MonoEyebrow } from "@akili/auth-kit";
import { cn } from "@/lib/utils";
import { V2Accent } from "./v2-accent";
import { V2Tiles, type V2Tile } from "./v2-tiles";

export interface V2ProofPaneProps {
  /** The bordered mono pill above everything. */
  eyebrow: ReactNode;
  /** The iris mono line between the eyebrow and the headline. */
  kicker?: ReactNode;
  /** The headline's plain words. The headline is the page's one h1. */
  lead: ReactNode;
  /** The headline's gradient words, rendered after `lead` inside the same h1. */
  accent?: ReactNode;
  subtitle?: ReactNode;
  /** A second, smaller paragraph under the subtitle. */
  note?: ReactNode;
  /** Frosted value + label tiles under the copy. */
  tiles?: readonly V2Tile[];
  /** Anything after the tiles (the sign-in page's demo tile). */
  children?: ReactNode;
  /** `start` in the split layout's left column; `center` above a centered card. */
  align?: "start" | "center";
  /** Prefix for `<prefix>-proof-pane` and `<prefix>-proof-tiles`. */
  testIdPrefix?: string;
}

/**
 * The statement half of a V2 page: eyebrow, kicker, the lead + accent h1,
 * subtitle and proof tiles. In `V2Page variant="split"` it is the `aside`;
 * in `centered` it sits above the card.
 */
export function V2ProofPane({
  eyebrow,
  kicker,
  lead,
  accent,
  subtitle,
  note,
  tiles,
  children,
  align = "start",
  testIdPrefix = "v2",
}: V2ProofPaneProps) {
  const centered = align === "center";
  return (
    <div
      className={cn("text-auth-text dark:text-auth-on-ink max-w-[52ch]", centered && "mx-auto text-center")}
      data-testid={`${testIdPrefix}-proof-pane`}
    >
      <MonoEyebrow variant="pill">{eyebrow}</MonoEyebrow>
      {kicker ? <p className="font-auth-mono text-auth-eyebrow text-auth-iris mt-6 mb-3.5 uppercase">{kicker}</p> : null}
      <h1 className={cn("text-auth-hero max-w-[16ch] font-semibold text-balance", centered && "mx-auto", !kicker && "mt-6")}>
        {lead}
        {accent ? (
          <>
            {" "}
            <V2Accent>{accent}</V2Accent>
          </>
        ) : null}
      </h1>
      {subtitle ? (
        <p
          className={cn(
            "text-auth-mute dark:text-auth-on-ink-mute mt-6 max-w-[48ch] text-[16px] leading-relaxed",
            centered && "mx-auto",
          )}
        >
          {subtitle}
        </p>
      ) : null}
      {note ? (
        <p
          className={cn(
            "text-auth-mute dark:text-auth-on-ink-mute mt-3.5 max-w-[52ch] text-[14px] leading-relaxed",
            centered && "mx-auto",
          )}
        >
          {note}
        </p>
      ) : null}
      {tiles && tiles.length > 0 ? (
        <V2Tiles tiles={tiles} surface="glass" className="mt-7" testId={`${testIdPrefix}-proof-tiles`} />
      ) : null}
      {children}
    </div>
  );
}
